import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EventSeatOutlinedIcon from '@mui/icons-material/EventSeatOutlined';
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ToolTip } from 'components/Tooltip/ToolTip';
import React, { FC, useMemo } from 'react';
import strings from '../../../../../../../common/Translation/Translate';
import { VenueConfigEmptyState } from '../../../VenueConfigEmptyState';
import { venueConfigDeleteIconProps } from '../../../venueConfigTableStyles';
import { getBaseUrl } from '../../../../../../../utils/validators/HelperFunctions';
import { buildConfiguredByLayoutId, isCapacityComplete } from './layoutMappingUtils';

export type VenueConfigLayoutItem = {
  id: number;
  name: string;
  imgUrl?: string;
};

type MappingLayoutCardsProps = {
  layoutItems: VenueConfigLayoutItem[];
  editedData: any[];
  capacityErrors?: Record<number, string>;
  onCapacityChange: (
    layoutId: number,
    layoutName: string,
    field: 'minCapacity' | 'maxCapacity',
    value: string,
  ) => void;
  onCapacityBlur: (layoutId: number) => void;
  onClear: (layoutId: number) => void;
};

const LayoutThumbnail: FC<{ name: string; imgUrl?: string }> = ({ name, imgUrl }) => {
  const theme = useTheme();
  if (imgUrl) {
    return (
      <CardMedia
        component="img"
        image={getBaseUrl() + imgUrl}
        alt={name}
        sx={{
          width: 56,
          height: 56,
          borderRadius: 1,
          objectFit: 'cover',
          flexShrink: 0,
          border: `1px solid ${theme.palette.divider}`,
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: 1,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        color: 'primary.main',
        border: `1px solid ${theme.palette.divider}`,
      }}>
      <EventSeatOutlinedIcon fontSize="medium" />
    </Box>
  );
};

export const MappingLayoutCards: FC<MappingLayoutCardsProps> = ({
  layoutItems,
  editedData,
  capacityErrors = {},
  onCapacityChange,
  onCapacityBlur,
  onClear,
}) => {
  const theme = useTheme();
  const configuredByLayoutId = useMemo(() => buildConfiguredByLayoutId(editedData), [editedData]);

  if (layoutItems.length === 0) {
    return <VenueConfigEmptyState message={strings.venueConfig_mapping_layouts_empty} />;
  }

  return (
    <Grid container spacing={2}>
      {layoutItems.map(layoutItem => {
        const layoutId = Number(layoutItem.id);
        const configured = configuredByLayoutId.get(layoutId);
        const minCapacity = configured?.minCapacity ?? '';
        const maxCapacity = configured?.maxCapacity ?? '';
        const isActive = isCapacityComplete(minCapacity, maxCapacity);
        const error = capacityErrors[layoutId];
        const hasError = Boolean(error);

        return (
          <Grid item xs={12} sm={6} md={4} key={layoutId} id={`venue-config-layout-${layoutId}`}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                transition: 'box-shadow 0.2s, border-color 0.2s, background-color 0.2s',
                borderStyle: isActive ? 'solid' : 'dashed',
                borderColor: hasError ? 'error.main' : isActive ? 'success.main' : 'divider',
                borderWidth: isActive ? 2 : 1,
                bgcolor: isActive
                  ? alpha(theme.palette.success.main, 0.08)
                  : 'background.paper',
                '&:hover': { boxShadow: isActive ? 3 : 2 },
              }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  <LayoutThumbnail name={layoutItem.name} imgUrl={layoutItem.imgUrl} />
                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={0.5}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap title={layoutItem.name} sx={{ flex: 1 }}>
                        {layoutItem.name}
                      </Typography>
                      {isActive && (
                        <ToolTip title={strings.venueConfig_mapping_layout_clear}>
                          <IconButton
                            size="small"
                            onClick={() => onClear(layoutId)}
                            aria-label="remove layout"
                            sx={{ mt: -0.5, mr: -0.5 }}>
                            <DeleteOutlineOutlinedIcon {...venueConfigDeleteIconProps} />
                          </IconButton>
                        </ToolTip>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color={isActive ? 'success.main' : 'text.secondary'}
                      sx={{ display: 'block', mt: 0.25 }}>
                      {isActive
                        ? strings.venueConfig_mapping_layout_configured
                        : strings.venueConfig_mapping_layout_set_capacity}
                    </Typography>
                    <Box display="flex" gap={1} mt={1.5} alignItems="flex-start">
                      <TextField
                        label={strings.venueConfig_mapping_minCapacity}
                        size="small"
                        type="number"
                        value={minCapacity}
                        onChange={e =>
                          onCapacityChange(layoutId, layoutItem.name, 'minCapacity', e.target.value)
                        }
                        onBlur={() => onCapacityBlur(layoutId)}
                        error={hasError}
                        inputProps={{ min: 1 }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label={strings.venueConfig_mapping_maxCapacity}
                        size="small"
                        type="number"
                        value={maxCapacity}
                        onChange={e =>
                          onCapacityChange(layoutId, layoutItem.name, 'maxCapacity', e.target.value)
                        }
                        onBlur={() => onCapacityBlur(layoutId)}
                        error={hasError}
                        inputProps={{ min: 1 }}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    {hasError && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {error}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};
